import { z } from 'zod'

import { idSchema, isoDateSchema, moneyPaisa, sortSchema } from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * A26 · `GET /receivables` — money still owed, and how long it has been owed
 * (screens 05 and 15).
 *
 * Ageing is the point. "Rs 25,000 outstanding" is a fact; "Rs 25,000
 * outstanding for 30 days from a buyer who owes on two other animals" is a
 * decision. The buckets are computed server-side from the server's own today,
 * so two devices in different timezones cannot disagree about what is overdue.
 */
export const RECEIVABLE_SORTS = ['-daysOutstanding', 'daysOutstanding', '-outstanding'] as const

export const receivableListQuery = cursorQuerySchema.extend({
  buyerPartyId: idSchema.optional(),
  /** Only rows aged at least this many days — the "overdue" chip. */
  minDaysOutstanding: z.coerce.number().int().nonnegative().optional(),
  sort: sortSchema(RECEIVABLE_SORTS, '-daysOutstanding'),
})

export const receivableRowSchema = z.object({
  saleId: idSchema,
  animalId: idSchema,
  animalLabel: z.string(),
  buyerPartyId: idSchema.nullish(),
  buyerName: z.string().nullish(),
  soldOn: isoDateSchema,
  daysOutstanding: z.number().int().nonnegative(),
  salePricePaisa: moneyPaisa(),
  paidPaisa: moneyPaisa(),
  outstandingPaisa: moneyPaisa(),
})

/** `0-30`, `31-60`, `61-90`, `90+` — named by the server so both apps agree. */
export const receivableAgeingBucketSchema = z.object({
  bucket: z.enum(['0-30', '31-60', '61-90', '90+']),
  count: z.number().int().nonnegative(),
  amountPaisa: moneyPaisa(),
})

export const receivablesResponse = z.object({
  asOf: isoDateSchema,
  totalOutstandingPaisa: moneyPaisa(),
  ageing: z.array(receivableAgeingBucketSchema),
  items: z.array(receivableRowSchema),
})

export type ReceivableListQuery = z.input<typeof receivableListQuery>
export type ReceivableRow = z.infer<typeof receivableRowSchema>
export type ReceivablesResponse = z.infer<typeof receivablesResponse>
